import { useEffect, useRef, useState } from "react";

/**
 * Coinflow hosted-checkout iframe (donor `coinflow.ts` contract):
 * postMessage from `coinflow.cash` / `*.coinflow.cash` signals success,
 * error, account linking and content height. The iframe fills its
 * container absolutely (fill mode) so Coinflow scrolls internally —
 * required for iOS Safari, which paints percent-height iframes at 0px
 * inside flex containers.
 */

export type CoinflowMessage =
  | { kind: "success"; paymentId?: string }
  | { kind: "error"; message: string }
  | { kind: "heightChange"; height: number }
  | { kind: "accountLinked"; accountType?: string }
  | { kind: "bankAuthCompleted" }
  | { kind: "unknown" };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export function isCoinflowOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);
    return hostname === "coinflow.cash" || hostname.endsWith(".coinflow.cash");
  } catch {
    return false;
  }
}

const readString = (value: unknown): string =>
  typeof value === "string" ? value : "";

function readMessageId(value: unknown): string {
  // Coinflow sends `paymentId` as both string and number in the wild.
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

export function parseCoinflowMessage(raw: unknown): CoinflowMessage {
  let data = raw;
  if (typeof raw === "string") {
    if (raw === "coinflow:bankAuthCompleted") {
      return { kind: "bankAuthCompleted" };
    }
    try {
      data = JSON.parse(raw);
    } catch {
      if (raw === "success") return { kind: "success" };
      if (raw === "error") return { kind: "error", message: "Payment failed" };
      if (raw === "accountLinked") return { kind: "accountLinked" };
      return { kind: "unknown" };
    }
  }

  if (!isRecord(data)) return { kind: "unknown" };

  const eventValue =
    readString(data.method) || readString(data.type) || readString(data.data);
  const info = isRecord(data.info) ? data.info : {};

  if (eventValue === "success") {
    return { kind: "success", paymentId: readMessageId(info.paymentId) };
  }
  if (eventValue === "error") {
    return {
      kind: "error",
      message:
        readString(data.message) ||
        readString(info.message) ||
        "Payment failed",
    };
  }
  if (eventValue === "accountLinked") {
    return { kind: "accountLinked", accountType: readString(info.type) };
  }
  if (eventValue === "heightChange") {
    const height = Number(data.height ?? data.data);
    return Number.isFinite(height) && height > 0
      ? { kind: "heightChange", height }
      : { kind: "unknown" };
  }
  return { kind: "unknown" };
}

const STALL_TIMEOUT_MS = 120_000;

export function CoinflowFrame({
  url,
  title,
  onMessage,
}: {
  url: string;
  title: string;
  onMessage: (message: CoinflowMessage) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [stalled, setStalled] = useState(false);
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    setLoading(true);
    setStalled(false);

    const iframe = document.createElement("iframe");
    iframe.src = url;
    iframe.style.position = "absolute";
    iframe.style.inset = "0";
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.border = "none";
    iframe.style.background = "transparent";
    iframe.allow = "payment; clipboard-write";
    iframe.title = title;

    // Drop the courtesy spinner on whichever fires first: iframe `load`,
    // the first Coinflow postMessage (cross-origin `load` never fires on
    // iOS Safari), or a 1.5s backstop.
    const dropSpinner = () => setLoading(false);
    iframe.addEventListener("load", dropSpinner);
    const spinnerBackstop = window.setTimeout(dropSpinner, 1500);

    const stallTimer = window.setTimeout(
      () => setStalled(true),
      STALL_TIMEOUT_MS,
    );

    const handler = (event: MessageEvent) => {
      // The bank-auth return page is same-origin and posts a plain string.
      const sameOrigin = event.origin === window.location.origin;
      if (!isCoinflowOrigin(event.origin) && !sameOrigin) return;
      dropSpinner();
      const message = parseCoinflowMessage(event.data);
      if (sameOrigin && message.kind !== "bankAuthCompleted") return;
      if (message.kind === "unknown" || message.kind === "heightChange") {
        return;
      }
      onMessageRef.current(message);
    };
    window.addEventListener("message", handler);

    container.innerHTML = "";
    container.appendChild(iframe);

    return () => {
      window.removeEventListener("message", handler);
      window.clearTimeout(spinnerBackstop);
      window.clearTimeout(stallTimer);
      container.innerHTML = "";
    };
  }, [title, url]);

  return (
    <div className="cashier-frame">
      {loading ? (
        <div className="cashier-frame__loading" role="status">
          <span aria-hidden className="cashier-frame__spinner" />
          <span>Loading checkout...</span>
        </div>
      ) : null}
      {stalled ? (
        <div className="cashier-frame__notice">
          This authorization is taking longer than usual. Complete any bank or
          SMS step, or go back and try again.
        </div>
      ) : null}
      <div className="cashier-frame__mount" ref={containerRef} />
    </div>
  );
}
